import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useApplications } from "@/hooks/useApplications";
import { useAppConfigurations } from "@/hooks/useAppConfigurations";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit, Copy, Trash2, Upload, CheckCircle, Clock, Plus, FileCode, XCircle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { AppConfigurationDialog } from "@/components/applications/config/AppConfigurationDialog";
import CustomerTag from "@/components/CustomerTag";
import { mockCustomers } from "@/utils/customerGroups";
import { TagsDisplay } from "@/components/tags/TagsDisplay";
import { PPPC_SERVICE_LABELS } from "@/types/application";
import { toast } from "sonner";
import { PlatformIcons } from "@/components/profiles/PlatformIcons";
import { Platform } from "@/types/profile";

export default function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { applications, removeApplication } = useApplications();
  const { configurations, createConfiguration, updateConfiguration, deleteConfiguration, toggleConfiguration } = useAppConfigurations(id);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAppConfigDialog, setShowAppConfigDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [deleteConfigDialog, setDeleteConfigDialog] = useState<any>(null);
  
  const application = applications.find(app => app.id === id);

  if (!application) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-12 text-center">
              <h2 className="text-2xl font-semibold mb-2">Application Not Found</h2>
              <p className="text-muted-foreground mb-6">
                The application you're looking for doesn't exist or has been removed.
              </p>
              <Button onClick={() => navigate("/applications")}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Applications
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const customerNames = application.customers
    .map(id => mockCustomers.find(c => c.id === id)?.name || "")
    .filter(name => name !== "");

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    await removeApplication(application.id);
    toast.success(`${application.name} deleted successfully`);
    navigate("/applications");
  };

  const handleDuplicate = () => {
    toast.success("Application duplicated");
  };

  const handlePublish = () => {
    toast.success("Application published");
  };

  const handleEditConfig = (configId: string) => {
    const config = configurations.find(c => c.id === configId);
    if (config) setEditingConfig(config);
  };

  const handleDuplicateConfig = async (configId: string) => {
    const config = configurations.find(c => c.id === configId);
    if (config) {
      await createConfiguration({
        applicationId: config.applicationId,
        name: `${config.name} (Copy)`,
        description: config.description,
        configurationData: config.configurationData,
        customers: config.customers,
        isActive: false,
      });
    }
  };

  const handleToggleConfig = async (configId: string) => {
    const config = configurations.find(c => c.id === configId);
    if (config) {
      await toggleConfiguration(configId, !config.isActive);
    }
  };

  const handleDeleteConfig = (configId: string) => {
    const config = configurations.find(c => c.id === configId);
    if (config) setDeleteConfigDialog(config);
  };

  const confirmDeleteConfig = async () => {
    if (deleteConfigDialog) {
      await deleteConfiguration(deleteConfigDialog.id);
      setDeleteConfigDialog(null);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-card/30">
        {/* Row 1: Navigation and Actions */}
        <div className="flex items-center justify-between px-6 py-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/applications")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Applications
          </Button>
          
          <ActionsDropdown
            align="end"
            actions={[
              {
                label: "Edit",
                icon: Edit,
                onClick: () => navigate(`/applications`),
              },
              {
                label: "Add App Configuration",
                icon: FileCode,
                onClick: () => setShowAppConfigDialog(true),
              },
              {
                label: "Duplicate",
                icon: Copy,
                onClick: handleDuplicate,
              },
              {
                label: "Publish",
                icon: Upload,
                onClick: handlePublish,
              },
              {
                label: "Delete",
                icon: Trash2,
                onClick: handleDelete,
                destructive: true,
              },
            ]}
          />
        </div>

        {/* Row 2: Application Details */}
        <div className="flex items-center justify-between px-6 pb-8 pt-4">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
              <img src={application.icon} alt={application.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-semibold">{application.name}</h1>
              <p className="text-sm text-muted-foreground mt-1">{application.bundleId}</p>
              <div className="flex items-center space-x-3 mt-3">
                <Badge>{application.version}</Badge>
                <PlatformIcons platforms={[application.platform as Platform]} size="sm" />
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-lg font-semibold">{customerNames.length}</div>
            <div className="text-xs text-muted-foreground">Deployments</div>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      {application.status === "published" ? (
        <div className="border-b p-4 bg-green-50 dark:bg-green-950/20">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-500" />
            <span className="font-medium">Published</span>
            <span className="text-sm text-muted-foreground">
              • Deployed to {customerNames.length} customer{customerNames.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      ) : (
        <div className="border-b p-4 bg-yellow-50 dark:bg-yellow-950/20">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-500" />
            <span className="font-medium">Draft</span>
            <span className="text-sm text-muted-foreground">
              • This application has not been published yet
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* General Information */}
          <Card>
            <CardHeader>
              <CardTitle>General Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Name</label>
                  <p className="text-sm mt-1">{application.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Bundle ID</label>
                  <p className="text-sm mt-1 font-mono">{application.bundleId}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Version</label>
                  <p className="text-sm mt-1">{application.version}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Size</label>
                  <p className="text-sm mt-1">{application.size}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Release Date</label>
                  <p className="text-sm mt-1">{application.releaseDate}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Platform</label>
                  <div className="mt-1">
                    <PlatformIcons platforms={[application.platform as Platform]} size="sm" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Source</label>
                  <div className="mt-1">
                    <Badge variant="outline" className="capitalize">{application.source}</Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Categories</label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {application.categories?.map((cat, i) => (
                      <Badge key={i} variant="secondary">{cat}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          {application.description && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{application.description}</p>
              </CardContent>
            </Card>
          )}

          {/* PPPC Settings */}
          {application.pppcSettings && (
            <Card>
              <CardHeader>
                <CardTitle>Privacy Preferences</CardTitle>
                <CardDescription>
                  Privacy Preferences Policy Control (PPPC) settings for this application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Identifier Type</label>
                  <p className="text-sm mt-1 capitalize">{application.pppcSettings.identifierType}</p>
                </div>
                {application.pppcSettings.codeRequirement && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Code Requirement</label>
                    <p className="text-sm mt-1 font-mono text-xs break-all bg-muted p-2 rounded">
                      {application.pppcSettings.codeRequirement}
                    </p>
                  </div>
                )}
                {application.pppcSettings.privacyPermissions && application.pppcSettings.privacyPermissions.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground block mb-2">Permissions</label>
                    <div className="space-y-2">
                      {application.pppcSettings.privacyPermissions.map((perm, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-muted rounded">
                          <span className="text-sm">{PPPC_SERVICE_LABELS[perm.serviceIdentifier]}</span>
                          <Badge variant={perm.allowed ? "default" : "secondary"}>
                            {perm.allowed ? "Allowed" : "Denied"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Assigned Customers */}
          <Card>
            <CardHeader>
              <CardTitle>Assigned Customers</CardTitle>
              <CardDescription>
                Customers who have access to this application
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {customerNames.length > 0 ? (
                  customerNames.map((name, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CustomerTag customer={name} />
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground italic">No customers assigned</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
              <CardDescription>
                Tags associated with this application
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TagsDisplay resourceId={application.id} resourceType="application" />
            </CardContent>
          </Card>

          {/* App Configurations */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>App Configurations</CardTitle>
                  <CardDescription>
                    Managed app configuration policies
                  </CardDescription>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowAppConfigDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {configurations.length > 0 ? (
                <div className="space-y-2">
                  {configurations.map(config => (
                    <div key={config.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{config.name}</p>
                            {config.isActive && (
                              <Badge variant="default" className="text-xs">Active</Badge>
                            )}
                          </div>
                          {config.description && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {config.description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>{config.configurationData.keys.length} keys</span>
                            <span>•</span>
                            <span>{config.customers.length} customers</span>
                          </div>
                        </div>
                        <ActionsDropdown
                          actions={[
                            {
                              label: "Edit",
                              icon: Edit,
                              onClick: () => handleEditConfig(config.id)
                            },
                            {
                              label: "Duplicate",
                              icon: Copy,
                              onClick: () => handleDuplicateConfig(config.id)
                            },
                            {
                              label: config.isActive ? "Deactivate" : "Activate",
                              icon: config.isActive ? XCircle : CheckCircle,
                              onClick: () => handleToggleConfig(config.id)
                            },
                            {
                              label: "Delete",
                              icon: Trash2,
                              onClick: () => handleDeleteConfig(config.id),
                              destructive: true
                            }
                          ]}
                        />
                      </div>
                      
                      <div className="mt-3 pt-3 border-t">
                        <div className="space-y-1.5">
                          {config.configurationData.keys.slice(0, 3).map(kvp => (
                            <div key={kvp.key} className="flex items-center justify-between text-xs">
                              <span className="font-mono text-muted-foreground">{kvp.key}</span>
                              <span className="text-muted-foreground truncate max-w-[150px]">
                                {String(kvp.value)}
                              </span>
                            </div>
                          ))}
                          {config.configurationData.keys.length > 3 && (
                            <p className="text-xs text-muted-foreground">
                              +{config.configurationData.keys.length - 3} more
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-muted-foreground">
                    No app configurations yet
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Add configuration to manage app settings
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{application.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* App Configuration Dialogs */}
      <AppConfigurationDialog
        open={showAppConfigDialog}
        onOpenChange={setShowAppConfigDialog}
        onSave={createConfiguration}
        applicationId={application.id}
      />

      {editingConfig && (
        <AppConfigurationDialog
          open={true}
          onOpenChange={(open) => !open && setEditingConfig(null)}
          onSave={(config) => updateConfiguration(editingConfig.id, config)}
          applicationId={application.id}
          editConfig={editingConfig}
        />
      )}

      {/* Delete Config Dialog */}
      <AlertDialog open={!!deleteConfigDialog} onOpenChange={(open) => !open && setDeleteConfigDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfigDialog?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteConfig}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
